import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { DashboardPoll, ConversationItem } from "./types";
import {
  getAllPolls,
  getFormResponses,
  getRespondentId,
  getVoterId,
  VOTES_STORAGE_KEY,
} from "@core/lib/pollStorage";
import { getCurrentUserId } from "@core/lib/guestIdentityService";
import { getConversations as getLocalConversations } from "@core/lib/storage/ConversationStorageSimple";
import { logError, ErrorFactory } from "@core/lib/error-handling";
import { useAuth } from "@core/contexts/AuthContext";
import { logger } from "@core/lib/logger";
import { isE2ETestingEnvironment } from "@core/lib/e2e-detection";
import type { Conversation } from "@core/types/conversation";
import type { Vote } from "@core/lib/pollStorage";

// NOTE: Les fonctionnalités Quizz ont été migrées vers apps/quizz/src/lib/quizz-service.ts
// Ce hook ne gère plus les quiz - utiliser le service dédié dans l'app quizz
import { supabase } from "@core/lib/supabase";
import { computeFormCardStats } from "./formCardStats";

type VoteWithData = Vote & {
  vote_data?: Record<string, "yes" | "no" | "maybe">;
  selections?: Record<string, "yes" | "no" | "maybe">;
};

// Redesign V2 — canonical insights layer (see packages/core/src/lib/insights).
import { normalizePollSlots } from "@core/lib/insights/normalizeSlots";
import { computeSlotScores } from "@core/lib/insights/computeScores";
import {
  formatDate,
  formatHourMinute,
} from "@core/components/voting/utils/dateUtils";
import type { PollVote } from "@core/hooks/usePollVotes";

export function useDashboardData(refreshKey: number) {
  const { t } = useTranslation("common");
  const [conversationItems, setConversationItems] = useState<
    ConversationItem[]
  >([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Log détaillé pour comprendre pourquoi user n'est pas disponible
  logger.debug(
    "🔍 Dashboard - useDashboardData - user depuis useAuth()",
    "dashboard",
    {
      hasUser: !!user,
      userId: user?.id || null,
      userEmail: user?.email || null,
      userObject: user ? { id: user.id, email: user.email } : null,
    },
  );

  // Vérifier si la détection E2E est désactivée explicitement
  const isE2EDetectionDisabled =
    typeof window !== "undefined" &&
    (window as Window & { __DISABLE_E2E_DETECTION__?: boolean })
      .__DISABLE_E2E_DETECTION__ === true;

  const isE2ETestMode =
    typeof window !== "undefined" &&
    !isE2EDetectionDisabled && // Si désactivé explicitement, ne pas considérer comme E2E
    (isE2ETestingEnvironment() ||
      (window as Window & { __IS_E2E_TESTING__?: boolean })
        .__IS_E2E_TESTING__ === true);

  logger.debug("🔍 Dashboard - isE2ETestMode calculé", "dashboard", {
    isE2ETestMode,
    isE2EDetectionDisabled,
    isE2ETestingEnvironment: isE2ETestingEnvironment(),
    hasUser: !!user,
    userId: user?.id || null,
  });

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Load conversations with early filtering based on user type
   */
  const loadConversations = useCallback(
    async (
      userId: string | null,
      isGuest: boolean,
    ): Promise<Conversation[]> => {
      if (isGuest) {
        // Guest: localStorage only, filter immediately
        const localConvs = getLocalConversations();
        const filteredConvs = localConvs.filter(
          (c) => c.userId === "guest" || c.userId === undefined,
        );

        console.log("🔍 DASHBOARD - Debug conversations:");
        console.log(
          "- Toutes les conversations (localStorage):",
          localConvs.length,
        );
        console.log(
          "- Conversations filtrées (guest/undefined):",
          filteredConvs.length,
        );
        console.log(
          "- Conversations filtrées IDs:",
          filteredConvs.map((c) => ({ id: c.id, userId: c.userId })),
        );

        return filteredConvs;
      }

      // Connected: Supabase + localStorage merge
      try {
        const { getConversations: getSupabaseConversations } =
          await import("@core/lib/storage/ConversationStorageSupabase");
        const supabaseConversations = await getSupabaseConversations(userId!);
        const localConversations = getLocalConversations();

        // Merge and filter by userId in one pass
        const mergedMap = new Map<string, Conversation>();

        supabaseConversations.forEach((conv) => {
          if (conv.userId === userId) {
            mergedMap.set(conv.id, conv);
          }
        });

        localConversations.forEach((localConv) => {
          if (localConv.userId === userId && !mergedMap.has(localConv.id)) {
            mergedMap.set(localConv.id, localConv);
          }
        });

        const merged = Array.from(mergedMap.values());

        // Update localStorage cache
        const { saveConversations } =
          await import("@core/lib/storage/ConversationStorageSimple");
        saveConversations(merged);

        return merged;
      } catch (supabaseError) {
        logger.error(
          "❌ Dashboard - Erreur Supabase, fallback localStorage",
          "dashboard",
          supabaseError,
        );
        return getLocalConversations().filter((c) => c.userId === userId);
      }
    },
    [],
  );

  /**
   * Load polls based on user type
   */
  const loadPolls = useCallback(
    async (userId: string | null, isGuest: boolean): Promise<any[]> => {
      try {
        if (isGuest) {
          const { getGuestPolls } =
            await import("@core/lib/guestIdentityService");
          let polls = await getGuestPolls();

          // E2E Test Support: Also read polls from test-specific localStorage keys
          if (isE2ETestMode && typeof window !== "undefined") {
            try {
              const testPollsKeys = [
                "availability-polls",
                "doodates_polls",
                "polls",
              ];
              const testPolls: any[] = [];

              testPollsKeys.forEach((key) => {
                const raw = localStorage.getItem(key);
                if (raw) {
                  try {
                    const parsed = JSON.parse(raw);
                    if (Array.isArray(parsed)) {
                      testPolls.push(...parsed);
                    }
                  } catch (parseError) {
                    logger.warn(
                      "Invalid JSON in localStorage key",
                      "dashboard",
                      { key, error: parseError },
                    );
                  }
                }
              });

              // Merge with deduplication
              const allPollsMap = new Map();
              [...polls, ...testPolls].forEach((poll) => {
                if (poll && poll.id) {
                  allPollsMap.set(poll.id, poll);
                }
              });

              polls = Array.from(allPollsMap.values());

              logger.debug(
                "🔍 E2E Dashboard - Polls chargés depuis localStorage",
                "dashboard",
                {
                  testPollsKeys,
                  totalPolls: polls.length,
                  hasAvailabilityPoll: polls.some((p) =>
                    p.title?.includes("Disponibilité Test E2E"),
                  ),
                },
              );
            } catch (testError) {
              logger.warn(
                "⚠️ Erreur lecture polls E2E localStorage",
                "dashboard",
                { testError },
              );
            }
          }

          return polls;
        }

        const { supabaseRestApi } = await import("@core/lib/supabaseApi");
        const rows = await supabaseRestApi<any[]>("polls", "GET", {
          query: {
            select: "*",
            creator_id: `eq.${userId}`,
            order: "created_at.desc",
          },
          timeout: 8000,
        });
        return (rows || []).map((poll: any) => ({
          ...poll.poll_data_json,
          id: poll.id,
          slug: poll.slug ?? poll.poll_data_json?.slug,
          type: poll.type ?? poll.poll_data_json?.type,
          // La colonne DB `status` fait foi (cf. PollService : `data.status ||
          // pollData.status`) — un poll_data_json.status obsolète ou absent ne
          // doit jamais l'emporter, sinon le badge du dashboard peut afficher
          // "Actif" pour un sondage réellement en brouillon (ex. duplicata).
          status: poll.status ?? poll.poll_data_json?.status,
          created_at: poll.created_at,
          updated_at: poll.updated_at,
        }));
      } catch (error) {
        logger.warn(
          "⚠️ Erreur récupération polls Supabase, fallback localStorage",
          "dashboard",
          {
            error,
          },
        );
        return getAllPolls().filter((poll) =>
          isGuest ? true : poll.creator_id === userId,
        );
      }
    },
    [isE2ETestMode],
  );

  /**
   * Fetch votes from Supabase for date polls
   */
  const fetchDatePollVotes = useCallback(
    async (pollIds: string[]): Promise<Map<string, any[]>> => {
      if (pollIds.length === 0) return new Map();

      try {
        // B3 fix : inclure 'form' (et date) — sinon les cards form-polls
        // restent à 0/0 (les réponses form ne sont jamais fetchées).
        // B3 fix : inclure is_test NULL (les votes réels ne settent pas
        // is_test → NULL). `.eq("is_test", false)` les excluait → cards à 0/0.
        const { data, error } = await supabase
          .from("poll_responses")
          .select("*")
          .in("poll_id", pollIds)
          .in("response_type", ["date", "form"])
          .or("is_test.is.null,is_test.eq.false");

        if (error) {
          logger.error(
            "Error fetching date poll votes from Supabase",
            "dashboard",
            { error },
          );
          return new Map();
        }

        const votesByPollId = new Map<string, any[]>();
        for (const vote of data || []) {
          const pollId = vote.poll_id;
          if (!votesByPollId.has(pollId)) {
            votesByPollId.set(pollId, []);
          }
          votesByPollId.get(pollId)!.push(vote);
        }

        return votesByPollId;
      } catch (err) {
        logger.error("Exception fetching date poll votes", "dashboard", {
          error: err,
        });
        return new Map();
      }
    },
    [],
  );

  /**
   * Index votes by poll_id for O(1) lookup (localStorage fallback)
   */
  const indexVotesByPollId = useCallback((): Map<string, Vote[]> => {
    const votesRaw = localStorage.getItem(VOTES_STORAGE_KEY);
    const localVotes: Vote[] = votesRaw ? JSON.parse(votesRaw) : [];

    const votesByPollId = new Map<string, Vote[]>();
    for (const vote of localVotes) {
      const pollId = vote.poll_id;
      if (pollId) {
        if (!votesByPollId.has(pollId)) {
          votesByPollId.set(pollId, []);
        }
        votesByPollId.get(pollId)!.push(vote);
      }
    }
    return votesByPollId;
  }, []);

  /**
   * Calculate poll statistics
   */
  const calculatePollStats = useCallback(
    (
      polls: any[],
      votesByPollId: Map<string, Vote[]>,
      supabaseVotesByPollId?: Map<string, any[]>,
    ): DashboardPoll[] => {
      return polls.map((poll) => {
        if (poll?.type === "form") {
          // B3 fix : compter depuis Supabase (poll_responses) au lieu de
          // localStorage getFormResponses (vide depuis migration B7).
          // Logique unique-count extraite dans formCardStats (testée).
          const formResps = supabaseVotesByPollId?.get(poll.id) || [];
          const stats = computeFormCardStats(formResps);
          return {
            ...poll,
            participants_count: stats.participantsCount,
            votes_count: stats.votesCount,
          };
        }

        // Pour les sondages de date, utiliser les votes Supabase
        if (poll?.type === "date" && supabaseVotesByPollId) {
          const supabaseVotes = supabaseVotesByPollId.get(poll.id) || [];
          const uniqueVoters = new Set(
            supabaseVotes.map(
              (vote) =>
                vote.respondent_email || vote.respondent_name || vote.device_id,
            ),
          ).size;

          let topDates: { date: string; score: number }[] = [];
          const selectedDates = poll.settings?.selectedDates || poll.dates;

          if (
            selectedDates &&
            Array.isArray(selectedDates) &&
            supabaseVotes.length > 0
          ) {
            const dateScoresMap = new Map<string, number>();

            for (let index = 0; index < selectedDates.length; index++) {
              const dateStr = selectedDates[index];
              const dateLabel =
                typeof dateStr === "string"
                  ? dateStr
                  : typeof dateStr === "object" &&
                      dateStr !== null &&
                      ("label" in dateStr || "title" in dateStr)
                    ? (dateStr as { label?: string; title?: string }).label ||
                      (dateStr as { label?: string; title?: string }).title ||
                      String(dateStr)
                    : String(dateStr);
              const optionId = (index + 1).toString();

              let score = 0;
              for (const vote of supabaseVotes) {
                const responseData = vote.response_data || {};
                const selection = responseData[optionId];
                if (selection === "yes") score += 3;
                else if (selection === "maybe") score += 1;
              }

              if (score > 0) {
                dateScoresMap.set(dateLabel, score);
              }
            }

            topDates = Array.from(dateScoresMap.entries())
              .map(([date, score]) => ({ date, score }))
              .sort((a, b) => b.score - a.score)
              .slice(0, 2);
          }

          // Redesign V2 — canonical topSlot via shared insights layer.
          let topSlot: DashboardPoll["topSlot"] = null;
          try {
            const slots = normalizePollSlots(poll);
            // Supabase stores keys as "1","2","3" (legacy VotingSwipe format).
            // normalizePollSlots generates "option-0","option-1"… — remap before scoring.
            const normalizedVotes = supabaseVotes.map((vote) => {
              const rd: Record<string, unknown> = vote.response_data || {};
              const remapped: Record<string, unknown> = {};
              for (const [k, v] of Object.entries(rd)) {
                const n = parseInt(k, 10);
                if (!isNaN(n) && n > 0 && k === String(n)) {
                  remapped[`option-${n - 1}`] = v;
                } else {
                  remapped[k] = v;
                }
              }
              return { ...vote, response_data: remapped };
            });
            const insights = computeSlotScores(
              slots,
              normalizedVotes as unknown as PollVote[],
            );
            if (insights.winnerSlotId) {
              const winner = slots.find((s) => s.id === insights.winnerSlotId);
              const score = insights.scores.find(
                (s) => s.slotId === insights.winnerSlotId,
              );
              if (winner && score) {
                let timeLabel: string | null = null;
                const ts = winner.timeSlots;
                if (ts && ts.length > 0) {
                  const first = ts[0];
                  const dur = first.duration ?? 30;
                  const startTime = formatHourMinute(first.hour, first.minute);
                  const endHour =
                    first.hour + Math.floor((first.minute + dur) / 60);
                  const endMin = (first.minute + dur) % 60;
                  const endTime = formatHourMinute(endHour, endMin);
                  timeLabel =
                    ts.length > 1
                      ? `${startTime} - ${endTime} (+${ts.length - 1})`
                      : `${startTime} - ${endTime}`;
                }
                topSlot = {
                  label: winner.groupLabel ?? formatDate(winner.primaryDate),
                  timeLabel,
                  yes: score.yes,
                  maybe: score.maybe,
                  no: score.no,
                  total: score.total,
                };
              }
            }
          } catch (e) {
            // Safe fallback: no topSlot. Existing topDates still works.
            logger.warn(
              "useDashboardData: topSlot computation failed",
              "dashboard",
              { pollId: poll.id, error: e },
            );
          }

          return {
            ...poll,
            participants_count: uniqueVoters,
            votes_count: supabaseVotes.length,
            topDates,
            topSlot,
          };
        }

        // Fallback pour les autres types ou si pas de votes Supabase
        const pollVotes = votesByPollId.get(poll.id) || [];
        const uniqueVoters = new Set(pollVotes.map((vote) => getVoterId(vote)))
          .size;

        let topDates: { date: string; score: number }[] = [];
        const selectedDates = poll.settings?.selectedDates || poll.dates;

        if (
          selectedDates &&
          Array.isArray(selectedDates) &&
          pollVotes.length > 0 &&
          poll.type === "date"
        ) {
          const dateScoresMap = new Map<string, number>();

          for (let index = 0; index < selectedDates.length; index++) {
            const dateStr = selectedDates[index];
            const dateLabel =
              typeof dateStr === "string"
                ? dateStr
                : typeof dateStr === "object" &&
                    dateStr !== null &&
                    ("label" in dateStr || "title" in dateStr)
                  ? (dateStr as { label?: string; title?: string }).label ||
                    (dateStr as { label?: string; title?: string }).title ||
                    String(dateStr)
                  : String(dateStr);
            const optionId = `option-${index}`;

            let score = 0;
            for (const vote of pollVotes) {
              const voteData =
                (vote as VoteWithData).vote_data ||
                (vote as VoteWithData).selections;
              const selection = voteData?.[optionId];
              if (selection === "yes") score += 3;
              else if (selection === "maybe") score += 1;
            }

            if (score > 0) {
              dateScoresMap.set(dateLabel, score);
            }
          }

          topDates = Array.from(dateScoresMap.entries())
            .map(([date, score]) => ({ date, score }))
            .sort((a, b) => b.score - a.score)
            .slice(0, 2);
        }

        return {
          ...poll,
          participants_count: uniqueVoters,
          votes_count: pollVotes.length,
          topDates,
        };
      });
    },
    [],
  );

  /**
   * Calculate quiz statistics
   * NOTE: Désactivé - Les quiz sont gérés par apps/quizz/src/lib/quizz-service.ts
   */
  // const calculateQuizzStats = useCallback((quizzes: Quizz[]): DashboardPoll[] => {
  //   return quizzes.map((quiz) => {
  //     // TODO: getQuizzResponses migré vers apps/quizz - besoin de refactorer ce hook
  //     const responses: any[] = []; // getQuizzResponses(quiz.id);
  //     const uniqueRespondents = new Set(
  //       responses.map((r) => r.deviceId || r.respondentEmail || r.id),
  //     ).size;

  //     return {
  //       id: quiz.id,
  //       creator_id: quiz.creator_id,
  //       title: quiz.title,
  //       description: quiz.description,
  //       slug: quiz.slug,
  //       status: quiz.status,
  //       created_at: quiz.created_at,
  //       updated_at: quiz.updated_at,
  //       type: "quizz" as const,
  //       dates: [],
  //       settings: {},
  //       participants_count: uniqueRespondents,
  //       votes_count: responses.length,
  //       relatedConversationId: quiz.relatedConversationId || quiz.conversationId,
  //     } as DashboardPoll;
  //   });
  // }, []);

  /**
   * Merge conversations and polls into unified items
   */
  const mergeConversationsAndPolls = useCallback(
    (
      conversations: Conversation[],
      allPolls: DashboardPoll[],
    ): ConversationItem[] => {
      const items: ConversationItem[] = conversations
        .map((conv) => {
          const metadata = conv.metadata;
          const pollId = conv.pollId || metadata?.pollId;
          const relatedPoll = pollId
            ? allPolls.find((p) => p.id === pollId)
            : undefined;

          return {
            id: conv.id,
            conversationTitle:
              conv.title || t("dashboard.untitledConversation"),
            conversationDate: new Date(
              conv.updatedAt || conv.createdAt || Date.now(),
            ),
            poll: relatedPoll,
            hasAI: !!metadata?.pollGenerated,
            tags: conv.tags || [],
            folderId: metadata?.folderId,
          } as ConversationItem;
        })
        .filter((item): item is ConversationItem => item !== null);

      // Add orphan polls (polls without conversations)
      const pollIdsWithConversation = new Set(
        conversations
          .map((conv) => conv.pollId || conv.metadata?.pollId)
          .filter((id): id is string => !!id),
      );

      const orphanPolls = allPolls.filter(
        (poll) => !pollIdsWithConversation.has(poll.id),
      );

      const orphanItems: ConversationItem[] = orphanPolls.map((poll) => ({
        id: `poll-${poll.id}`,
        conversationTitle: poll.title || t("dashboard.untitledPoll"),
        conversationDate: new Date(poll.updated_at || poll.created_at),
        poll: poll,
        hasAI: false,
        tags: [],
      }));

      return [...items, ...orphanItems];
    },
    [t],
  );

  // ============================================================================
  // MAIN LOAD DATA
  // ============================================================================

  const latestRunIdRef = useRef(0);

  const loadData = useCallback(async () => {
    const runId = ++latestRunIdRef.current;
    setLoading(true);

    const isGuest = !user?.id || isE2ETestMode;
    const userId = user?.id || null;

    logger.debug("🔍 Dashboard - loadData DÉBUT", "dashboard", {
      hasUser: !!user,
      userId,
      isGuest,
      isE2ETestMode,
      runId,
    });

    try {
      // 1. Load conversations and polls in parallel (optimized paths)
      const [conversations, polls] = await Promise.all([
        loadConversations(userId, isGuest),
        loadPolls(userId, isGuest),
      ]);

      // 2. NOTE: Quiz loading désactivé - migré vers apps/quizz/src/lib/quizz-service.ts
      // TODO: getQuizz migré vers apps/quizz - besoin de refactorer ce hook
      // const currentUserId = getCurrentUserId(userId);
      // const allQuizz: Quizz[] = []; // getQuizz();
      // const quizzes = allQuizz.filter((quiz) =>
      //   isGuest ? quiz.creator_id === currentUserId : quiz.creator_id === userId,
      // );

      // 3. Fetch votes from Supabase for date + form polls (B3 fix).
      const datePollIds = polls
        .filter((p) => p.type === "date" || p.type === "form")
        .map((p) => p.id);
      const supabaseVotesByPollId = await fetchDatePollVotes(datePollIds);

      // 4. Calculate stats (with Supabase votes for date polls)
      const votesByPollId = indexVotesByPollId();
      const pollsWithStats = calculatePollStats(
        polls,
        votesByPollId,
        supabaseVotesByPollId,
      );
      // const quizzWithStats = calculateQuizzStats(quizzes);
      const allPolls = [...pollsWithStats]; // , ...quizzWithStats];

      // 4. Merge conversations + polls
      const items = mergeConversationsAndPolls(conversations, allPolls);

      // 5. Sort by date
      items.sort(
        (a, b) => b.conversationDate.getTime() - a.conversationDate.getTime(),
      );

      if (runId !== latestRunIdRef.current) {
        logger.debug("🚫 Dashboard - run obsolète ignoré", "dashboard", {
          runId,
          latest: latestRunIdRef.current,
          items: items.length,
          isGuest,
        });
        return;
      }

      setConversationItems(items);

      logger.info("✅ Dashboard loaded", "dashboard", {
        conversations: conversations.length,
        polls: allPolls.length,
        items: items.length,
        isGuest,
      });
    } catch (error) {
      // Network errors (Supabase unreachable) are expected in offline/test envs — log as warn, not critical
      const isNetworkError =
        error instanceof Error &&
        (error.message.includes("Failed to fetch") ||
          error.message.includes("ERR_NAME_NOT_RESOLVED") ||
          error.message.includes("NetworkError") ||
          error.name === "TypeError");
      if (isNetworkError) {
        logger.warn("Dashboard data unavailable (network error)", "dashboard", {
          originalError: error instanceof Error ? error.message : String(error),
        });
      } else {
        logError(
          ErrorFactory.storage(
            "Failed to load dashboard data",
            t("dashboard.loadError"),
          ),
          { component: "useDashboardData", metadata: { originalError: error } },
        );
      }
    } finally {
      if (runId === latestRunIdRef.current) {
        setLoading(false);
      }
    }
    // Bug #8 — clé primitive `user?.id` (pas l'objet user) pour éviter un
    // re-fetch à chaque event onAuthStateChange (INITIAL_SESSION puis
    // SIGNED_IN livrent un nouvel objet user mais même id).
  }, [
    user?.id,
    isE2ETestMode,
    loadConversations,
    loadPolls,
    fetchDatePollVotes,
    indexVotesByPollId,
    calculatePollStats,
    // calculateQuizzStats, // Désactivé - migré vers apps/quizz
    mergeConversationsAndPolls,
    t,
  ]);

  useEffect(() => {
    loadData();
  }, [refreshKey, loadData]);

  return { conversationItems, loading, reload: loadData };
}
